import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentsIcon from "@mui/icons-material/Payments";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SavingsIcon from "@mui/icons-material/Savings";
import SettingsIcon from "@mui/icons-material/Settings";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import {
  analyzeThreeDCProfile,
  generateThreeDCReport,
  getThreeDCHistory,
  getThreeDCLlmSettings,
  getThreeDCRun,
  getThreeDCReportUrl,
  getThreeDCStatus,
  getThreeDCWorkflows,
  runThreeDCConnector,
  saveThreeDCWorkflow,
} from "./threeDCApi";
import "./ThreeDC.scss";

const COLORS = ["#2768d5", "#27a6ad", "#7b6fd0", "#e09551", "#24a16d", "#8da0ba"];

const MODULES = {
  identity: ["Identity & consent", "Validate identity and permission scope.", "required"],
  sources: ["Source validation", "Confirm expected evidence bundles.", "required"],
  social: ["Social discovery", "Normalize public and permissioned social activity.", "core"],
  bank: ["Bank connect", "Load consented banking records.", "core"],
  engagement: ["Engagement", "Measure recorded interaction frequency.", "analysis"],
  interests: ["Topic occurrence", "Count recorded topic activity.", "analysis"],
  intent: ["Purchase-related records", "List explicit purchase-related activity.", "analysis"],
  employment: ["Recurring income", "Calculate recurring deposit patterns.", "analysis"],
  spend: ["Spend patterns", "Calculate cash flow and category composition.", "analysis"],
  sentiment: ["Sentiment & behavior", "Analyze expressed sentiment and bounded patterns.", "analysis"],
  ofac: ["OFAC screening", "Screen the supplied name against sanctions data.", "connector"],
  darkweb: ["Dark web scan", "Check configured breach-intelligence providers.", "connector"],
  profile: ["Factual evidence report", "Create the source-linked AI report.", "required"],
  report: ["Insight report", "Generate the report artifact.", "output"],
};

const MODULE_ORDER = Object.keys(MODULES);
const REQUIRED_MODULES = ["identity", "sources", "profile"];
const PRODUCT_OPTIONS = [
  ["all", "All products"],
  ["lending", "Lending"],
  ["cards", "Cards"],
  ["loyalty", "Loyalty"],
  ["payments", "Payments"],
  ["savings", "Savings"],
  ["wealth", "Wealth"],
  ["insurance", "Insurance"],
  ["remittance", "Remittance"],
  ["financial_wellness", "Financial wellness"],
];

const PERSONAS = {
  maya: {
    label: "Maya Chen · Urban professional",
    profile: {
      name: "Maya Chen",
      date_of_birth: "",
      street: "",
      city: "Toronto",
      province: "ON",
      country: "Canada",
      company_name: "Northern Arc Software",
      linkedin: "",
      facebook: "",
      consent: {
        social_analysis: true,
        bank_analysis: true,
        screening_analysis: true,
        profiling_analysis: true,
        product_personalization: true,
        captured_at: "2026-06-01T14:20:00Z",
      },
      social_handles: { instagram: "@mayamoves", tiktok: "@mayamoves", reddit: "u/urbanmornings" },
    },
    social: {
      events: 1842,
      sources: 4,
      months: 6,
      engagementRate: 7.8,
      interests: [["Travel", 92], ["Wellness", 86], ["Sustainable fashion", 78], ["Coffee culture", 72], ["Personal finance", 65]],
      brands: [["Lululemon", 88], ["Air Canada", 81], ["Sephora", 76], ["Apple", 71]],
      timeline: [
        ["Instagram", "Saved a carousel about carry-on travel essentials", "Positive"],
        ["TikTok", "Engaged with three reformer Pilates reviews", "Positive"],
        ["Reddit", "Compared high-interest savings account options", "Neutral"],
        ["Public web", "Reviewed an electric compact SUV comparison", "Positive"],
      ],
    },
    bank: {
      connector: "Northstar Bank",
      accounts: [["Everyday Chequing", 4286], ["Momentum Savings", 18750]],
      income: 6840,
      spend: 4725,
      months: [["Jan", 6710, 4550], ["Feb", 6840, 4620], ["Mar", 6840, 4880], ["Apr", 7010, 4705], ["May", 6840, 4930], ["Jun", 6840, 4725]],
      categories: [["Housing", 36], ["Dining", 16], ["Travel", 15], ["Shopping", 13], ["Wellness", 9], ["Other", 11]],
      recurring: [
        ["Northern Arc Software", "Payroll", 6840, "Monthly"],
        ["Harbour Property Mgmt", "Housing", -1720, "Monthly"],
        ["Momentum Savings", "Savings transfer", -1250, "Monthly"],
      ],
    },
  },
  liam: {
    label: "Liam Brooks · Young family",
    profile: {
      name: "Liam Brooks",
      age_range: "35-39",
      date_of_birth: "",
      street: "",
      city: "Mississauga",
      province: "ON",
      country: "Canada",
      email: "liam.brooks@example.test",
      company_name: "Transway Logistics",
      linkedin: "",
      facebook: "",
      consent: {
        social_analysis: true,
        bank_analysis: true,
        screening_analysis: true,
        profiling_analysis: true,
        product_personalization: true,
        captured_at: "2026-06-02T10:05:00Z",
      },
      social_handles: { instagram: "@brooksbuilds", reddit: "u/weekendfixer" },
    },
    social: {
      events: 1186,
      sources: 3,
      months: 6,
      engagementRate: 5.9,
      interests: [["Home improvement", 94], ["Family activities", 88], ["Automotive", 74], ["Barbecue", 69], ["Investing", 61]],
      brands: [["Home Depot", 92], ["Costco", 87], ["Toyota", 79], ["Weber", 73]],
      timeline: [
        ["Instagram", "Shared a backyard renovation before-and-after", "Positive"],
        ["Reddit", "Compared three-row hybrid SUVs", "Neutral"],
        ["YouTube", "Watched deck-building and tool reviews", "Positive"],
        ["Public web", "Browsed family resort packages", "Positive"],
      ],
    },
    bank: {
      connector: "Northstar Bank",
      accounts: [["Family Chequing", 5980.11], ["Rainy Day Savings", 26400]],
      income: 8920,
      spend: 7035,
      months: [["Jan", 8750, 6760], ["Feb", 8920, 6890], ["Mar", 8920, 7310], ["Apr", 9100, 6980], ["May", 8920, 7460], ["Jun", 8920, 7035]],
      categories: [["Housing", 42], ["Groceries", 18], ["Transport", 14], ["Family", 11], ["Home", 9], ["Other", 6]],
      recurring: [
        ["Transway Logistics", "Payroll", 7220, "Monthly"],
        ["Secondary payroll", "Payroll", 1700, "Monthly"],
        ["Mortgage payment", "Housing", -2780, "Monthly"],
        ["Education savings", "Savings transfer", -650, "Monthly"],
      ],
    },
  },
  neil: {
    label: "Neil Appalsamy · Business owner",
    profile: {
      name: "Neil Appalsamy",
      date_of_birth: "1971-02-01",
      street: "42 Freeston Cres",
      city: "Ajax",
      province: "ON",
      country: "Canada",
      company_name: "Valance Telecom Group Inc.",
      linkedin: "https://ca.linkedin.com/in/neilappalsamy",
      facebook: "User reported",
      reported_income: { annual_average_cad: 240000, monthly_equivalent_cad: 20000, type: "Consulting income", provenance: "User provided" },
      consent: {
        social_analysis: true,
        bank_analysis: true,
        screening_analysis: true,
        profiling_analysis: true,
        product_personalization: true,
        captured_at: "2026-06-09T00:00:00-04:00",
      },
      social_handles: { linkedin: "https://ca.linkedin.com/in/neilappalsamy", facebook: "User reported" },
    },
    social: {
      events: 5,
      sources: 3,
      months: 0,
      engagementRate: 0,
      interests: [["Financial inclusion", 86], ["Telecommunications", 78], ["Business leadership", 72]],
      brands: [["4PAY", 90], ["Valance", 88]],
      timeline: [
        ["LinkedIn", "Public profile lists Ajax, Ontario and 4PAY Inc.", "Unclassified"],
        ["LinkedIn", "Public activity includes financial-inclusion strategy.", "Unclassified"],
        ["Public web", "Company leadership reference for Valance Telecom Group Inc.", "Unclassified"],
      ],
    },
    bank: {
      connector: "TD Bank",
      accounts: [["TD Chequing", null], ["TD Mortgage", 0]],
      income: 20000,
      spend: 13750,
      months: [["Jul 24", 19400, 12800], ["Oct 24", 20800, 14200], ["Jan 25", 20100, 13100], ["Apr 25", 21600, 14900], ["Jul 25", 19600, 13500], ["Oct 25", 21000, 14600], ["Jan 26", 20300, 13200], ["Jun 26", 20000, 13750]],
      categories: [["Credit card", 51], ["Travel", 15], ["Dining", 12], ["Visa Debit", 10], ["e-Transfer", 7], ["Household bills", 5]],
      recurring: [
        ["Consulting business", "Deposit", 20000, "Monthly average"],
        ["TD Visa", "Credit-card payment", -10000, "Monthly average"],
        ["Rogers Wireless", "Bill payment", -190, "Monthly"],
        ["Hydro", "Bill payment", -270, "Monthly"],
        ["Town of Ajax", "Property taxes", -1650, "Quarterly"],
      ],
    },
  },
};

const emptyFocus = () => ({
  market_segment: "General consumer",
  product_focus: ["all"],
  themes: [],
  keywords: [],
  topics: [],
  instructions: "",
});

const normalizeFocus = (focus = {}) => ({
  ...emptyFocus(),
  ...focus,
  product_focus: focus.product_focus?.length ? focus.product_focus : ["all"],
  themes: focus.themes || [],
  keywords: focus.keywords || [],
  topics: focus.topics || [],
});

const splitList = (value) =>
  [...new Set(String(value || "").split(",").map((item) => item.trim()).filter(Boolean))].slice(0, 20);

const money = (value) =>
  value === null || value === undefined
    ? "Not supplied"
    : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(value);

const dateLabel = (value) => {
  if (!value) return "In progress";
  const parsed = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat("en-CA", { dateStyle: "medium", timeStyle: "short" }).format(parsed);
};

const scoreAverage = (items, fallback = 55) => {
  const values = (items || []).map((item) => Number(item.score)).filter(Number.isFinite);
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : fallback;
};

function buildEvidence(persona, workflow) {
  const profile = {
    ...persona.profile,
    address: {
      street: persona.profile.street,
      city: persona.profile.city,
      province: persona.profile.province,
      country: persona.profile.country,
    },
  };
  const social = [
    ...persona.social.timeline.map((item, index) => ({
      id: `social-event-${index + 1}`,
      source: item[0],
      event: item[1],
      observed_sentiment: item[2],
      provenance: "synthetic_sample",
    })),
    ...persona.social.interests.map((item, index) => ({
      id: `topic-record-${index + 1}`,
      type: "normalized_topic_occurrence",
      topic: item[0],
      normalized_share: item[1],
      provenance: "synthetic_sample",
    })),
    ...persona.social.brands.map((item, index) => ({
      id: `brand-signal-${index + 1}`,
      type: "brand_engagement",
      brand: item[0],
      normalized_strength: item[1],
      provenance: "synthetic_sample",
    })),
  ];
  const bank = [
    ...persona.bank.months.map((item, index) => ({
      id: `cashflow-month-${index + 1}`,
      month: item[0],
      inflow: item[1],
      outflow: item[2],
      source: persona.bank.connector,
      provenance: "synthetic_sample",
    })),
    ...persona.bank.categories.map((item, index) => ({
      id: `spend-category-${index + 1}`,
      category: item[0],
      percent_of_spend: item[1],
      source: persona.bank.connector,
      provenance: "synthetic_sample",
    })),
    ...persona.bank.recurring.map((item, index) => ({
      id: `recurring-${index + 1}`,
      counterparty: item[0],
      normalized_type: item[1],
      amount: item[2],
      cadence: item[3],
      source: persona.bank.connector,
      provenance: "synthetic_sample",
    })),
  ];

  return {
    profile,
    workflow: {
      workflow_uuid: workflow.workflow_uuid,
      name: workflow.name,
      description: workflow.description,
      modules: workflow.modules,
      focus: normalizeFocus(workflow.focus),
    },
    social_evidence: social,
    bank_evidence: bank,
    ofac_evidence: [],
    darkweb_evidence: [],
  };
}

function FitIcon({ type }) {
  const icons = {
    micro: <PaymentsIcon />,
    mini: <SavingsIcon />,
    card: <CreditCardIcon />,
    travel: <TravelExploreIcon />,
    personal: <AccountBalanceIcon />,
    wealth: <AutoAwesomeIcon />,
  };
  return <span className={`threedc-fit-icon ${type}`}>{icons[type]}</span>;
}

function productFits(result, workflow) {
  const focus = normalizeFocus(workflow?.focus).product_focus;
  const focused = (tags) => focus.includes("all") || tags.some((tag) => focus.includes(tag));
  const adjust = (tags) => (focus.includes("all") ? 0 : focused(tags) ? 10 : -7);
  const completeness = Number(result?.evidence_completeness || 0);
  const lending = scoreAverage(result?.lending_support_indicators);
  const bnpl = scoreAverage(result?.bnpl_opportunities);
  const products = [
    ["micro", "Micro Loan", "$250 limit", 62 + bnpl * 0.18 + completeness * 0.12, ["lending", "financial_wellness"], "Low-limit liquidity aligned to short-duration cash-flow needs."],
    ["travel", "Travel Rewards Card", "Illustrative pre-qualified offer", 54 + scoreAverage(result?.lifestyle_preferences) * 0.28, ["cards", "loyalty"], "Rewards proposition aligned to recorded travel and engagement signals."],
    ["mini", "Mini Loan", "$2,500 limit", 50 + lending * 0.28, ["lending"], "Small instalment facility aligned to recurring inflow records."],
    ["personal", "Personal Loan", "Up to $15,000 review", 38 + lending * 0.38 + completeness * 0.08, ["lending"], "Larger credit review supported by recurring-income and cash-flow evidence."],
    ["card", "Credit Card Increase", "$8,000 to $12,000 review", 42 + lending * 0.32, ["cards", "lending"], "Limit review aligned to payment behavior and available financial capacity."],
    ["wealth", "Wealth Management", "Qualified referral", 34 + completeness * 0.2 + scoreAverage(result?.profile_dimensions) * 0.22, ["wealth", "savings"], "Advisory referral aligned to savings capacity and planning indicators."],
  ];
  return products
    .map(([id, name, offer, rawScore, tags, rationale]) => ({
      id,
      name,
      offer,
      score: Math.max(0, Math.min(100, Math.round(rawScore + adjust(tags)))),
      focusMatch: focused(tags),
      rationale,
    }))
    .sort((a, b) => b.score - a.score);
}

function Metric({ label, value, caption }) {
  return (
    <article className="threedc-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{caption}</small>
    </article>
  );
}

function IntelligenceCards({ items, empty = "No supported findings are available." }) {
  if (!items?.length) return <div className="threedc-empty">{empty}</div>;
  return (
    <div className="threedc-intelligence-grid">
      {items.map((item, index) => (
        <article key={`${item.label || item.dimension || item.pattern || item.fact || index}-${index}`} className="threedc-intelligence-card">
          <div>
            <strong>{item.label || item.dimension || item.pattern || item.source || item.fact || "Finding"}</strong>
            {Number.isFinite(Number(item.score)) && <span>{Number(item.score)}/100</span>}
          </div>
          {(item.insight || item.rationale || item.interpretation || item.summary || item.status) && (
            <p>{item.insight || item.rationale || item.interpretation || item.summary || item.status}</p>
          )}
          <small>
            {[item.confidence, item.evidence_class, item.limitations].filter(Boolean).join(" | ")}
            {item.evidence_ids?.length ? `${[item.confidence, item.evidence_class, item.limitations].some(Boolean) ? " | " : ""}${item.evidence_ids.length} evidence refs: ${item.evidence_ids.join(", ")}` : ""}
          </small>
        </article>
      ))}
    </div>
  );
}

function metricProgress(item) {
  const value = String(item?.value ?? "");
  const ratio = value.match(/([\d.]+)\s+of\s+([\d.]+)/i);
  if (ratio) return Math.min(100, (Number(ratio[1]) / Number(ratio[2])) * 100);
  const scored = value.match(/([\d.]+)\s*\/\s*100/);
  if (scored) return Math.min(100, Number(scored[1]));
  const percent = value.match(/([\d.]+)\s*%/);
  if (percent) return Math.min(100, Number(percent[1]));
  return Math.min(100, 18 + (item?.evidence_ids?.length || 0) * 12);
}

function CalculatedMetricCards({ items = [] }) {
  if (!items.length) return <div className="threedc-empty">No calculated metrics were saved.</div>;
  return (
    <div className="threedc-calculated-grid">
      {items.map((item, index) => (
        <article className="threedc-calculated-card" key={`${item.label}-${index}`}>
          <header>
            <span>{String(item.label || "M").charAt(0).toUpperCase()}</span>
            <strong>{item.label}</strong>
          </header>
          <h4>{item.value}</h4>
          <i><b style={{ width: `${metricProgress(item)}%` }} /></i>
          <div>
            <small>{item.method || "Deterministic calculation"}</small>
            <em>{item.evidence_ids?.length || 0} evidence refs</em>
          </div>
          {item.evidence_ids?.length ? <p>{item.evidence_ids.join(", ")}</p> : null}
        </article>
      ))}
    </div>
  );
}

function Collapsible({ title, count, children, openInitially = false }) {
  const [open, setOpen] = useState(openInitially);
  return (
    <section className={`threedc-collapse ${open ? "open" : ""}`}>
      <button type="button" onClick={() => setOpen((value) => !value)}>
        <strong>{title}</strong>
        <span>{count !== undefined ? `${count} items` : ""}<i>{open ? "−" : "+"}</i></span>
      </button>
      {open && <div className="threedc-collapse-content">{children}</div>}
    </section>
  );
}

function scoreForPattern(item) {
  if (Number.isFinite(Number(item?.score))) return Number(item.score);
  if (item?.confidence === "High") return 86;
  if (item?.confidence === "Medium") return 66;
  return 42;
}

function HistoryVisualIntelligence({ result, embedded = false }) {
  const dimensions = (result.profile_dimensions || []).slice(0, 6).map((item) => ({
    subject: item.dimension,
    score: Number(item.score || 0),
  }));
  const sentiment = result.sentiment_analysis || { score: 0, overall_label: "Insufficient data", summary: "" };
  const behaviors = (result.behavioral_patterns || []).slice(0, 5).map((item) => ({
    label: item.pattern,
    score: scoreForPattern(item),
  }));

  const charts = (
    <div className="threedc-history-visual-grid">
        <article>
          <h4>Profile dimensions</h4>
          {dimensions.length ? (
            <ResponsiveContainer width="100%" height={270}>
              <RadarChart data={dimensions} outerRadius="67%">
                <PolarGrid stroke="#c8d7ea" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "#526985", fontSize: 11 }} />
                <Radar dataKey="score" stroke="#2768d5" fill="#4a8ae2" fillOpacity={0.38} animationDuration={900} />
              </RadarChart>
            </ResponsiveContainer>
          ) : <div className="threedc-empty">No dimension scores were saved.</div>}
        </article>
        <article>
          <h4>Sentiment score</h4>
          <div className="threedc-history-sentiment">
            <div style={{ "--score": `${Math.max(0, Math.min(100, sentiment.score || 0)) * 3.6}deg` }}>
              <strong>{sentiment.score || 0}</strong><span>/100</span>
            </div>
            <strong>{sentiment.overall_label}</strong>
            <p>{sentiment.summary}</p>
          </div>
        </article>
        <article>
          <h4>Behavioral strength</h4>
          <div className="threedc-behavior-dials">
            {behaviors.length ? behaviors.map((item) => (
              <div key={item.label}>
                <span className="threedc-mini-dial" style={{ "--score": `${item.score * 3.6}deg` }}><strong>{item.score}</strong></span>
                <p>{item.label}</p>
                <i><b style={{ width: `${item.score}%` }} /></i>
              </div>
            )) : <div className="threedc-empty">No behavioral scores were saved.</div>}
          </div>
        </article>
    </div>
  );
  if (embedded) return charts;
  return (
    <section className="threedc-history-visuals">
      <div className="threedc-section-title"><span>Visual intelligence</span><h3>Saved profile signals</h3></div>
      {charts}
    </section>
  );
}

function HistoryTimeline({ timeline = [], selectedRunUuid }) {
  const chartData = timeline.map((item) => ({
    date: dateLabel(item.completed_at || item.created_at),
    completeness: Number(item.confidence || 0),
    sentiment: Number(item.result?.sentiment_analysis?.score || 0),
  }));

  return (
    <div className="threedc-history-timeline">
      <div className="threedc-section-title"><span>Profile timeline</span><h3>Compare intelligence over time</h3></div>
      {chartData.length > 1 ? (
        <article className="threedc-timeline-chart">
          <h4>Evidence completeness trend</h4>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="historyTrendFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#2768d5" stopOpacity=".38" />
                  <stop offset="1" stopColor="#2768d5" stopOpacity=".03" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e7f0" />
              <XAxis dataKey="date" tick={{ fill: "#647893", fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#647893", fontSize: 10 }} />
              <Tooltip />
              <Area type="monotone" dataKey="completeness" stroke="#2768d5" strokeWidth={3} fill="url(#historyTrendFill)" animationDuration={900} />
              <Area type="monotone" dataKey="sentiment" stroke="#1aa878" strokeWidth={2} fill="transparent" animationDuration={1000} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="threedc-chart-key"><span><i className="blue" />Completeness</span><span><i className="green" />Sentiment</span></div>
        </article>
      ) : <div className="threedc-empty">At least two completed runs are needed for a trend chart.</div>}
      <div className="threedc-timeline-cards">
        {timeline.map((item) => (
          <article className={item.run_uuid === selectedRunUuid ? "active" : ""} key={item.run_uuid}>
            <time>{dateLabel(item.completed_at || item.created_at)}</time>
            <strong>{item.confidence || 0}% complete</strong>
            <span>{item.result?.customer_segments?.[0]?.label || item.status}</span>
            <i><b style={{ width: `${item.confidence || 0}%` }} /></i>
          </article>
        ))}
      </div>
      <div className="threedc-timeline-table">
        <div><strong>Date</strong><strong>Completeness</strong><strong>Sentiment</strong><strong>Primary segment</strong><strong>Facts</strong><strong>Patterns</strong></div>
        {timeline.map((item) => (
          <div className={item.run_uuid === selectedRunUuid ? "active" : ""} key={`table-${item.run_uuid}`}>
            <span>{dateLabel(item.completed_at || item.created_at)}</span>
            <strong>{item.confidence ?? 0}%</strong>
            <span>{item.result?.sentiment_analysis?.overall_label || "--"}</span>
            <span>{item.result?.customer_segments?.[0]?.label || "Not captured"}</span>
            <span>{item.result?.observed_facts?.length || 0}</span>
            <span>{item.result?.behavioral_patterns?.length || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThreeDC() {
  const navigate = useNavigate();
  const [appTab, setAppTab] = useState("analyze");
  const [dashboardTab, setDashboardTab] = useState("overview");
  const [personaKey, setPersonaKey] = useState("maya");
  const [profile, setProfile] = useState(PERSONAS.maya.profile);
  const [editorMode, setEditorMode] = useState("fields");
  const [sourceTab, setSourceTab] = useState("profile");
  const [workflows, setWorkflows] = useState([]);
  const [workflowUuid, setWorkflowUuid] = useState("");
  const [workflowDraft, setWorkflowDraft] = useState(null);
  const [status, setStatus] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [runningStage, setRunningStage] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [reportBusy, setReportBusy] = useState("");
  const [message, setMessage] = useState("Initialize a consumer to begin.");
  const [error, setError] = useState("");
  const [history, setHistory] = useState({ items: [], pagination: { page: 1, page_count: 1, total: 0 }, filters: { statuses: [], models: [] } });
  const [historyQuery, setHistoryQuery] = useState({ page: 1, pageSize: 10, search: "", status: "", model: "" });
  const [selectedRun, setSelectedRun] = useState(null);
  const [historyDetailTab, setHistoryDetailTab] = useState("summary");
  const [llmSettings, setLlmSettings] = useState(null);
  const [selectedModel, setSelectedModel] = useState(window.localStorage.getItem("3dc.llmModel") || "");
  const [busy, setBusy] = useState(false);

  const persona = useMemo(() => ({ ...PERSONAS[personaKey], profile }), [personaKey, profile]);
  const workflow = useMemo(
    () => workflows.find((item) => item.workflow_uuid === workflowUuid) || workflows[0] || {
      workflow_uuid: "local-default",
      name: "Consumer Intelligence",
      description: "",
      modules: MODULE_ORDER.filter((key) => !["ofac", "darkweb"].includes(key)),
      focus: emptyFocus(),
    },
    [workflows, workflowUuid]
  );
  const stages = workflow.modules || [];
  const fitProducts = useMemo(() => productFits(analysis || {}, workflow), [analysis, workflow]);

  useEffect(() => {
    const load = async () => {
      try {
        const [statusData, workflowData] = await Promise.all([getThreeDCStatus(), getThreeDCWorkflows()]);
        setStatus(statusData);
        setWorkflows(workflowData.workflows || []);
        setWorkflowUuid((workflowData.workflows || [])[0]?.workflow_uuid || "");
        setSelectedModel((current) => current || statusData.ai?.model || "");
      } catch (loadError) {
        setError(loadError.message);
      }
    };
    load();
  }, []);

  useEffect(() => {
    setProfile(PERSONAS[personaKey].profile);
    setInitialized(false);
    setBankConnected(false);
    setCompleted([]);
    setAnalysis(null);
    setAnalysisMeta(null);
    setReportMeta(null);
    setMessage("Initialize a consumer to begin.");
  }, [personaKey]);

  const loadHistory = async (nextQuery = historyQuery) => {
    setBusy(true);
    try {
      const data = await getThreeDCHistory(nextQuery);
      setHistory(data);
      setHistoryQuery(nextQuery);
      setError("");
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setBusy(false);
    }
  };

  const changeAppTab = (tab) => {
    setAppTab(tab);
    if (tab === "history") loadHistory();
    if (tab === "workflows") setWorkflowDraft({ ...workflow, focus: normalizeFocus(workflow.focus) });
    if (tab === "llm") {
      getThreeDCLlmSettings()
        .then((data) => {
          setLlmSettings(data);
          setSelectedModel((current) => current || data.default_model);
        })
        .catch((loadError) => setError(loadError.message));
    }
  };

  const initialize = () => {
    setInitialized(true);
    setCompleted([]);
    setAnalysis(null);
    setAnalysisMeta(null);
    setReportMeta(null);
    setMessage("Profile ready. Run the remaining workflow stages.");
  };

  const connectBank = () => {
    setBankConnected(true);
    setMessage(`${persona.bank.connector} records connected.`);
  };

  const runRemaining = async () => {
    if (!initialized || busy) return;
    if (stages.includes("bank") && !bankConnected) {
      setSourceTab("bank");
      setMessage("Connect the selected bank records before continuing.");
      return;
    }

    setBusy(true);
    setError("");
    let nextCompleted = [...completed];
    let evidence = buildEvidence(persona, workflow);
    let activeRunUuid = analysisMeta?.run_uuid || "";

    try {
      for (const stage of stages) {
        if (nextCompleted.includes(stage)) continue;
        setRunningStage(stage);
        setMessage(`Running ${MODULES[stage]?.[0] || stage}...`);

        if (stage === "ofac") {
          const connector = await runThreeDCConnector("ofac", evidence.profile);
          evidence.ofac_evidence = connector.result ? [connector.result] : [];
        } else if (stage === "darkweb") {
          const connector = await runThreeDCConnector("darkweb", evidence.profile);
          evidence.darkweb_evidence = connector.result ? [connector.result] : [];
        } else if (stage === "profile") {
          const data = await analyzeThreeDCProfile({ ...evidence, model: selectedModel });
          setAnalysis(data.analysis);
          setAnalysisMeta(data.meta);
          activeRunUuid = data.meta?.run_uuid || "";
        } else if (stage === "report") {
          if (!activeRunUuid) throw new Error("A completed analysis is required before generating the report.");
          const data = await generateThreeDCReport(activeRunUuid);
          setReportMeta(data.report || null);
        } else {
          await new Promise((resolve) => setTimeout(resolve, 120));
        }

        nextCompleted = [...nextCompleted, stage];
        setCompleted(nextCompleted);
      }
      setMessage("Factual consumer intelligence report complete.");
      setDashboardTab("overview");
    } catch (runError) {
      setError(runError.message);
      setMessage(`Analysis stopped at ${MODULES[runningStage]?.[0] || runningStage}.`);
    } finally {
      setRunningStage("");
      setBusy(false);
    }
  };

  const reset = () => {
    setInitialized(false);
    setBankConnected(false);
    setCompleted([]);
    setAnalysis(null);
    setAnalysisMeta(null);
    setReportMeta(null);
    setMessage("Initialize a consumer to begin.");
    setError("");
  };

  const saveWorkflow = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await saveThreeDCWorkflow(workflowDraft);
      const workflowData = await getThreeDCWorkflows();
      setWorkflows(workflowData.workflows || []);
      setWorkflowUuid(data.workflow.workflow_uuid);
      setWorkflowDraft({ ...data.workflow, focus: normalizeFocus(data.workflow.focus) });
      setMessage("Workflow saved.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  };

  const selectRun = async (runUuid) => {
    setBusy(true);
    try {
      const data = await getThreeDCRun(runUuid);
      setSelectedRun(data);
      setHistoryDetailTab("summary");
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = async (runUuid) => {
    if (!runUuid || reportBusy) return;
    setReportBusy(runUuid);
    setError("");
    try {
      const data = await generateThreeDCReport(runUuid);
      const report = data.report || null;
      if (analysisMeta?.run_uuid === runUuid) setReportMeta(report);
      setSelectedRun((current) => current?.run?.run_uuid === runUuid
        ? { ...current, run: { ...current.run, report_uuid: report?.report_uuid, report_file: report?.file_name, report_url: report?.download_url } }
        : current);
      const link = document.createElement("a");
      link.href = getThreeDCReportUrl(runUuid);
      link.download = report?.file_name || "3dc-consumer-intelligence-report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (reportError) {
      setError(reportError.message);
    } finally {
      setReportBusy("");
    }
  };

  const updateConsent = (key) => {
    setProfile((current) => ({
      ...current,
      consent: { ...current.consent, [key]: !current.consent?.[key] },
    }));
  };

  const renderAnalyze = () => (
    <div className="threedc-analyze-layout">
      <aside className="threedc-panel threedc-source-panel">
        <div className="threedc-panel-heading">
          <div><span>Profile setup</span><h2>Consumer source</h2></div>
          <i>{initialized ? "Ready" : "Draft"}</i>
        </div>
        <label className="threedc-field">
          <span>Sample persona</span>
          <select value={personaKey} onChange={(event) => setPersonaKey(event.target.value)}>
            {Object.entries(PERSONAS).map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}
          </select>
        </label>
        <label className="threedc-field">
          <span>Analysis workflow</span>
          <select value={workflow.workflow_uuid} onChange={(event) => setWorkflowUuid(event.target.value)}>
            {workflows.map((item) => <option key={item.workflow_uuid} value={item.workflow_uuid}>{item.name}</option>)}
          </select>
          <small>{workflow.description}</small>
        </label>
        <div className="threedc-focus-chips">
          {[normalizeFocus(workflow.focus).market_segment, ...normalizeFocus(workflow.focus).product_focus].filter(Boolean).map((item) => <span key={item}>{item.replace(/_/g, " ")}</span>)}
        </div>
        <div className="threedc-segmented">
          {["profile", "social", "bank"].map((tab) => <button key={tab} className={sourceTab === tab ? "active" : ""} onClick={() => setSourceTab(tab)}>{tab}</button>)}
        </div>
        {sourceTab === "profile" && (
          <>
            <div className="threedc-segmented compact">
              <button className={editorMode === "fields" ? "active" : ""} onClick={() => setEditorMode("fields")}>Fields</button>
              <button className={editorMode === "json" ? "active" : ""} onClick={() => setEditorMode("json")}>JSON</button>
            </div>
            {editorMode === "fields" ? (
              <div className="threedc-profile-fields">
                {[
                  ["name", "Full name"], ["date_of_birth", "Date of birth"], ["street", "Street address"],
                  ["city", "City"], ["province", "Province"], ["country", "Country"],
                  ["company_name", "Company"], ["linkedin", "LinkedIn URL"], ["facebook", "Facebook URL or status"],
                ].map(([key, label]) => (
                  <label key={key} className={key === "street" || key === "linkedin" || key === "facebook" ? "wide" : ""}>
                    <span>{label}</span>
                    <input value={profile[key] || ""} onChange={(event) => setProfile((current) => ({ ...current, [key]: event.target.value }))} />
                  </label>
                ))}
              </div>
            ) : (
              <textarea className="threedc-json" value={JSON.stringify(profile, null, 2)} onChange={(event) => {
                try { setProfile(JSON.parse(event.target.value)); } catch { /* keep last valid JSON */ }
              }} />
            )}
          </>
        )}
        {sourceTab === "social" && (
          <div className="threedc-source-summary">
            <strong>{persona.social.events.toLocaleString()} recorded events</strong>
            <p>{persona.social.sources} sources · {persona.social.interests.length} normalized topics</p>
            {persona.social.interests.map((item) => <span key={item[0]}>{item[0]}</span>)}
          </div>
        )}
        {sourceTab === "bank" && (
          <div className="threedc-bank-connect">
            <div><AccountBalanceIcon /><span><strong>{persona.bank.connector}</strong><small>Consented demonstration records</small></span><i>{bankConnected ? "Connected" : "Ready"}</i></div>
            <button type="button" className="threedc-button primary" onClick={connectBank}><CheckIcon fontSize="small" /> {bankConnected ? "Records connected" : "Connect records"}</button>
            {persona.bank.accounts.map((account) => <p key={account[0]}><span>{account[0]}</span><strong>{money(account[1])}</strong></p>)}
          </div>
        )}
        <button type="button" className="threedc-button primary full" onClick={initialize}><PlayArrowIcon fontSize="small" /> Initialize</button>
      </aside>

      <div className="threedc-main-column">
        <section className="threedc-panel">
          <div className="threedc-pipeline-heading">
            <div><span>Analysis pipeline</span><h2>Consumer intelligence flow</h2><p>{message}</p></div>
            <button type="button" className="threedc-button success" disabled={!initialized || busy} onClick={runRemaining}><PlayArrowIcon fontSize="small" /> Run remaining</button>
          </div>
          <div className="threedc-progress"><span style={{ width: `${stages.length ? (completed.length / stages.length) * 100 : 0}%` }} /></div>
          <div className="threedc-stage-grid">
            {stages.map((key, index) => (
              <article key={key} className={`${completed.includes(key) ? "complete" : ""} ${runningStage === key ? "running" : ""}`}>
                <i>{completed.includes(key) ? <CheckIcon fontSize="inherit" /> : index + 1}</i>
                <strong>{MODULES[key]?.[0] || key}</strong>
                <span>{runningStage === key ? "Analyzing..." : completed.includes(key) ? "Complete" : key === "bank" && !bankConnected ? "Connect account" : "Pending"}</span>
              </article>
            ))}
          </div>
        </section>
        {renderDashboard()}
      </div>
    </div>
  );

  const renderDashboard = () => {
    const bank = persona.bank;
    const result = analysis;
    const dimensions = result?.profile_dimensions || [];
    const radarData = dimensions.length
      ? dimensions.slice(0, 6).map((item) => ({ subject: item.dimension, score: item.score }))
      : [["Transaction activity", 74], ["Cash-flow regularity", 92], ["Discretionary mix", 61], ["Digital engagement", 78], ["Evidence completeness", 72]].map(([subject, score]) => ({ subject, score }));
    const sentiment = result?.sentiment_analysis || { overall_label: "Pending", score: 0, summary: "Run the factual report to calculate expressed sentiment." };

    return (
      <section className="threedc-panel threedc-dashboard">
        <div className="threedc-dashboard-heading">
          <div><span>Profile output</span><h2>{result ? `${profile.name} · Consumer intelligence` : "Consumer intelligence dashboard"}</h2></div>
          <div><span>Evidence completeness</span><strong>{result ? `${result.evidence_completeness}%` : "--"}</strong></div>
        </div>
        <nav className="threedc-dashboard-tabs">
          {[
            ["overview", "Overview"], ["permissions", "Permissions"], ["social", "Social records"],
            ["banking", "Banking"], ["evidence", "Facts & evidence"], ["fit", "FIT"],
          ].map(([key, label]) => <button key={key} className={dashboardTab === key ? "active" : ""} onClick={() => setDashboardTab(key)}>{label}</button>)}
        </nav>
        {!result && dashboardTab !== "permissions" && (
          <div className="threedc-dashboard-empty">
            <AutoAwesomeIcon />
            <h3>Insights will appear here</h3>
            <p>Initialize the profile and run the workflow to create a source-linked factual record.</p>
          </div>
        )}
        {result && dashboardTab === "overview" && (
          <div className="threedc-dashboard-body">
            <div className="threedc-metric-grid">
              <Metric label="Monthly inflow" value={money(bank.income)} caption="Recurring deposits detected" />
              <Metric label="Monthly spend" value={money(bank.spend)} caption="Calculated from supplied records" />
              <Metric label="Social engagement" value={`${persona.social.engagementRate}%`} caption={`${persona.social.events.toLocaleString()} normalized events`} />
              <Metric label="Overall sentiment" value={sentiment.overall_label} caption={`${sentiment.score}/100 evidence score`} />
            </div>
            <article className="threedc-summary-card">
              <span>Consumer summary</span>
              <h3>{profile.name}</h3>
              <p>{result.executive_summary}</p>
              <small>{analysisMeta?.model} · Run {analysisMeta?.run_uuid?.slice(0, 8)} · Persisted</small>
              <div className="threedc-report-action">
                <span>{reportMeta?.file_name || "PDF report ready to generate"}</span>
                <button type="button" className="threedc-button primary" disabled={reportBusy === analysisMeta?.run_uuid} onClick={() => downloadReport(analysisMeta?.run_uuid)}>
                  <DownloadIcon fontSize="small" /> {reportBusy === analysisMeta?.run_uuid ? "Preparing..." : "Download report"}
                </button>
              </div>
            </article>
            <div className="threedc-visual-grid">
              <article>
                <h3>Profile dimensions</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <RadarChart data={radarData} outerRadius="68%">
                    <PolarGrid stroke="#c8d7ea" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: "#526985", fontSize: 10 }} />
                    <Radar dataKey="score" stroke="#2768d5" fill="#4a8ae2" fillOpacity={0.36} animationDuration={800} />
                  </RadarChart>
                </ResponsiveContainer>
              </article>
              <article className="threedc-sentiment-dial">
                <h3>Sentiment score</h3>
                <div style={{ "--score": `${sentiment.score * 3.6}deg` }}><strong>{sentiment.score}</strong><span>/100</span></div>
                <p>{sentiment.summary}</p>
              </article>
            </div>
          </div>
        )}
        {dashboardTab === "permissions" && (
          <div className="threedc-dashboard-body">
            <div className="threedc-permission-hero"><div><span>Consumer permission register</span><h3>Consent and processing permissions</h3><p>Purpose-specific permission status for each data source and intelligence activity.</p></div><strong>{Object.values(profile.consent || {}).filter((value) => value === true).length}/5 active</strong></div>
            <div className="threedc-permission-grid">
              {[
                ["social_analysis", "Social media analysis", "Public and permissioned social records"],
                ["bank_analysis", "Banking analysis", "Accounts, transactions and recurring deposits"],
                ["screening_analysis", "External screening", "OFAC and dark-web connector checks"],
                ["profiling_analysis", "Consumer intelligence profiling", "Behavior, topics and dimensionality"],
                ["product_personalization", "Product personalization", "FIT product and service alignment"],
              ].map(([key, title, scope]) => (
                <article key={key} className={profile.consent?.[key] ? "granted" : "denied"}>
                  <span><CheckIcon /></span><div><h4>{title}</h4><p>{scope}</p><small>Captured {profile.consent?.captured_at || "Not recorded"}</small></div>
                  <button onClick={() => updateConsent(key)}>{profile.consent?.[key] ? "Granted" : "Not granted"}</button>
                </article>
              ))}
            </div>
            <div className="threedc-rights"><strong>GDPR data-subject controls</strong><span>Access and copy</span><span>Rectification</span><span>Erasure</span><span>Restriction or objection</span><span>Data portability</span><span>Human review</span></div>
          </div>
        )}
        {result && dashboardTab === "social" && (
          <div className="threedc-dashboard-body threedc-two-column">
            <article className="threedc-chart-card"><h3>Recorded topics</h3>{persona.social.interests.map((item) => <div className="threedc-bar" key={item[0]}><span>{item[0]}</span><i><b style={{ width: `${item[1]}%` }} /></i><strong>{item[1]}</strong></div>)}</article>
            <article className="threedc-chart-card"><h3>Brand engagement</h3>{persona.social.brands.map((item) => <div className="threedc-bar" key={item[0]}><span>{item[0]}</span><i><b style={{ width: `${item[1]}%` }} /></i><strong>{item[1]}</strong></div>)}</article>
            <article className="threedc-chart-card wide"><h3>Social evidence timeline</h3>{persona.social.timeline.map((item, index) => <p key={`${item[0]}-${index}`}><strong>{item[0]}</strong><span>{item[1]}</span><i>{item[2]}</i></p>)}</article>
          </div>
        )}
        {result && dashboardTab === "banking" && (
          <div className="threedc-dashboard-body threedc-two-column">
            <article className="threedc-chart-card">
              <h3>Cash-flow trend</h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={bank.months.map((item) => ({ month: item[0], inflow: item[1], spend: item[2] }))}>
                  <defs><linearGradient id="inflowFill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#2768d5" stopOpacity=".4" /><stop offset="1" stopColor="#2768d5" stopOpacity=".02" /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e7f0" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip />
                  <Area type="monotone" dataKey="inflow" stroke="#2768d5" fill="url(#inflowFill)" animationDuration={800} />
                  <Area type="monotone" dataKey="spend" stroke="#e09551" fill="transparent" animationDuration={900} />
                </AreaChart>
              </ResponsiveContainer>
            </article>
            <article className="threedc-chart-card">
              <h3>Spend composition</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart><Pie data={bank.categories.map((item) => ({ name: item[0], value: item[1] }))} dataKey="value" innerRadius={54} outerRadius={88} paddingAngle={2} animationDuration={850}>{bank.categories.map((item, index) => <Cell key={item[0]} fill={COLORS[index % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <div className="threedc-legend">{bank.categories.map((item, index) => <span key={item[0]}><i style={{ background: COLORS[index % COLORS.length] }} />{item[0]} {item[1]}%</span>)}</div>
            </article>
            <article className="threedc-chart-card wide"><h3>Recurring records</h3>{bank.recurring.map((item) => <p key={item[0]}><strong>{item[0]}</strong><span>{item[1]}</span><b>{money(item[2])}</b><i>{item[3]}</i></p>)}</article>
          </div>
        )}
        {result && dashboardTab === "evidence" && (
          <div className="threedc-dashboard-body">
            <Collapsible title="Observed facts" count={result.observed_facts?.length}><IntelligenceCards items={result.observed_facts} /></Collapsible>
            <Collapsible title="Calculated metrics" count={result.calculated_metrics?.length} openInitially><CalculatedMetricCards items={result.calculated_metrics} /></Collapsible>
            <Collapsible title="Screening leads" count={result.screening_leads?.length}><IntelligenceCards items={result.screening_leads} /></Collapsible>
            <Collapsible title="Workflow focus assessment" count={result.workflow_focus_findings?.length}><IntelligenceCards items={result.workflow_focus_findings} /></Collapsible>
            <Collapsible title="Behavioral analysis" count={result.behavioral_patterns?.length}><IntelligenceCards items={result.behavioral_patterns} /></Collapsible>
            <Collapsible title="Profile dimensionality" count={result.profile_dimensions?.length}><IntelligenceCards items={result.profile_dimensions} /></Collapsible>
            <Collapsible title="Lifestyle and preferences" count={result.lifestyle_preferences?.length}><IntelligenceCards items={result.lifestyle_preferences} /></Collapsible>
            <Collapsible title="Customer segmentation" count={result.customer_segments?.length}><IntelligenceCards items={result.customer_segments} /></Collapsible>
            <Collapsible title="Personalization" count={result.personalization_opportunities?.length}><IntelligenceCards items={result.personalization_opportunities} /></Collapsible>
            <Collapsible title="Lending support indicators" count={result.lending_support_indicators?.length}><IntelligenceCards items={result.lending_support_indicators} /></Collapsible>
            <Collapsible title="BNPL opportunities" count={result.bnpl_opportunities?.length}><IntelligenceCards items={result.bnpl_opportunities} /></Collapsible>
          </div>
        )}
        {result && dashboardTab === "fit" && (
          <div className="threedc-dashboard-body">
            <div className="threedc-fit-hero"><div><span>Financial intelligence targeting</span><h3>Products and services aligned to the evidence</h3><p>Workflow product focus adjusts prioritization while factual evidence determines the score.</p></div><strong>{Math.round(fitProducts.reduce((sum, item) => sum + item.score, 0) / fitProducts.length)}/100</strong></div>
            <div className="threedc-fit-grid">
              {fitProducts.map((item) => <article key={item.id}><FitIcon type={item.id} /><span>{item.focusMatch ? "Focus match" : "Further review"}</span><h3>{item.name}</h3><strong>{item.offer}</strong><div><i><b style={{ width: `${item.score}%` }} /></i><em>{item.score}</em></div><p>{item.rationale}</p></article>)}
            </div>
            <p className="threedc-boundary">Decision-support demonstration only. No approval, eligibility, credit-limit or adverse-action decision is generated.</p>
          </div>
        )}
      </section>
    );
  };

  const renderHistory = () => {
    const run = selectedRun?.run;
    const result = run?.result || {};
    const timeline = selectedRun?.timeline || [];
    const timelineIndex = timeline.findIndex((item) => item.run_uuid === run?.run_uuid);
    const priorRun = timelineIndex > 0 ? timeline[timelineIndex - 1] : null;
    const confidenceChange = priorRun ? Number(run?.confidence || 0) - Number(priorRun.confidence || 0) : null;
    const sentimentItems = result.sentiment_analysis ? [{
      label: result.sentiment_analysis.overall_label || "Recorded sentiment",
      score: result.sentiment_analysis.score,
      insight: result.sentiment_analysis.summary,
      evidence_ids: result.sentiment_analysis.evidence_ids || [],
      limitations: result.sentiment_analysis.limitations,
    }] : [];
    return (
      <section className="threedc-panel threedc-page-panel">
        <div className="threedc-page-heading"><div><span>Saved intelligence</span><h2>3D C analysis history</h2><p>Persisted consumer runs from the selected backend.</p></div><button className="threedc-button" onClick={() => loadHistory()}><RefreshIcon fontSize="small" /> Refresh</button></div>
        <form className="threedc-history-filters" onSubmit={(event) => { event.preventDefault(); loadHistory({ ...historyQuery, page: 1 }); }}>
          <input placeholder="Consumer name or run ID" value={historyQuery.search} onChange={(event) => setHistoryQuery((current) => ({ ...current, search: event.target.value }))} />
          <select value={historyQuery.status} onChange={(event) => setHistoryQuery((current) => ({ ...current, status: event.target.value }))}><option value="">All statuses</option>{(history.filters?.statuses || []).map((item) => <option key={item}>{item}</option>)}</select>
          <select value={historyQuery.model} onChange={(event) => setHistoryQuery((current) => ({ ...current, model: event.target.value }))}><option value="">All models</option>{(history.filters?.models || []).map((item) => <option key={item}>{item}</option>)}</select>
          <button className="threedc-button primary">Apply</button>
        </form>
        <div className="threedc-history-layout">
          <div className="threedc-history-list">
            <small>{history.pagination?.total || 0} saved analyses</small>
            {(history.items || []).map((item) => <button key={item.run_uuid} className={run?.run_uuid === item.run_uuid ? "active" : ""} onClick={() => selectRun(item.run_uuid)}><span><strong>{item.display_name}</strong><small>{item.run_uuid.slice(0, 8)}</small></span><span>{item.model || "Pending"}</span><i>{item.status}</i><time>{dateLabel(item.completed_at || item.created_at)}</time></button>)}
            <div className="threedc-pagination"><button disabled={history.pagination?.page <= 1} onClick={() => loadHistory({ ...historyQuery, page: history.pagination.page - 1 })}>Previous</button><span>Page {history.pagination?.page || 1} of {history.pagination?.page_count || 1}</span><button disabled={history.pagination?.page >= history.pagination?.page_count} onClick={() => loadHistory({ ...historyQuery, page: history.pagination.page + 1 })}>Next</button></div>
          </div>
          <div className="threedc-history-detail">
            {!run ? <div className="threedc-empty">Select a saved analysis to recall its complete detail.</div> : (
              <>
                <div className="threedc-history-summary">
                  <span>Saved result</span>
                  <h3>{run.display_name} · Detailed intelligence</h3>
                  <small>{dateLabel(run.completed_at || run.created_at)}</small>
                  <div><Metric label="Status" value={run.status} /><Metric label="Completeness" value={`${run.confidence ?? 0}%`} /><Metric label="Observed facts" value={result.observed_facts?.length || 0} /><Metric label="Change from prior" value={confidenceChange === null ? "--" : `${confidenceChange >= 0 ? "+" : ""}${confidenceChange} pts`} /></div>
                  {run.status === "complete" && (
                    <div className="threedc-report-action">
                      <span>{run.report_file || "No PDF has been generated for this run"}</span>
                      <button type="button" className="threedc-button primary" disabled={reportBusy === run.run_uuid} onClick={() => downloadReport(run.run_uuid)}>
                        <DownloadIcon fontSize="small" /> {reportBusy === run.run_uuid ? "Preparing..." : run.report_url ? "Download report" : "Generate & download"}
                      </button>
                    </div>
                  )}
                </div>
                <nav className="threedc-detail-tabs">{["summary", "timeline", "evidence", "behavior", "customer", "decisions", "fit"].map((tab) => <button key={tab} className={historyDetailTab === tab ? "active" : ""} onClick={() => setHistoryDetailTab(tab)}>{tab}</button>)}</nav>
                {historyDetailTab === "summary" && <div className="threedc-detail-pane"><p>{result.executive_summary}</p><Collapsible title="Visual intelligence" count={3}><HistoryVisualIntelligence result={result} embedded /></Collapsible><Collapsible title="Source coverage" count={result.source_coverage?.length}><IntelligenceCards items={result.source_coverage} /></Collapsible><Collapsible title="Data gaps" count={result.data_gaps?.length}><div className="threedc-gap-list">{(result.data_gaps || []).map((item) => <span key={item}>{item}</span>)}</div></Collapsible><div className="threedc-history-footer">Run: {run.run_uuid}<br />Model: {run.model || "--"}</div></div>}
                {historyDetailTab === "timeline" && <div className="threedc-detail-pane"><HistoryTimeline timeline={timeline} selectedRunUuid={run.run_uuid} /></div>}
                {historyDetailTab === "evidence" && <div className="threedc-detail-pane"><Collapsible title="Observed facts" count={result.observed_facts?.length}><IntelligenceCards items={result.observed_facts} /></Collapsible><Collapsible title="Calculated metrics" count={result.calculated_metrics?.length} openInitially><CalculatedMetricCards items={result.calculated_metrics} /></Collapsible><Collapsible title="Screening leads" count={result.screening_leads?.length}><IntelligenceCards items={result.screening_leads} /></Collapsible></div>}
                {historyDetailTab === "behavior" && <div className="threedc-detail-pane"><Collapsible title="Sentiment" count={sentimentItems.length}><IntelligenceCards items={sentimentItems} /></Collapsible><Collapsible title="Behavioral patterns" count={result.behavioral_patterns?.length}><IntelligenceCards items={result.behavioral_patterns} /></Collapsible><Collapsible title="Profile dimensions" count={result.profile_dimensions?.length}><IntelligenceCards items={result.profile_dimensions} /></Collapsible></div>}
                {historyDetailTab === "customer" && <div className="threedc-detail-pane"><Collapsible title="Workflow focus assessment" count={result.workflow_focus_findings?.length}><IntelligenceCards items={result.workflow_focus_findings} /></Collapsible><Collapsible title="Lifestyle and preferences" count={result.lifestyle_preferences?.length}><IntelligenceCards items={result.lifestyle_preferences} /></Collapsible><Collapsible title="Customer segments" count={result.customer_segments?.length}><IntelligenceCards items={result.customer_segments} /></Collapsible><Collapsible title="Personalization opportunities" count={result.personalization_opportunities?.length}><IntelligenceCards items={result.personalization_opportunities} /></Collapsible></div>}
                {historyDetailTab === "decisions" && <div className="threedc-detail-pane"><Collapsible title="Lending support indicators" count={result.lending_support_indicators?.length}><IntelligenceCards items={result.lending_support_indicators} /></Collapsible><Collapsible title="BNPL opportunities" count={result.bnpl_opportunities?.length}><IntelligenceCards items={result.bnpl_opportunities} /></Collapsible><p className="threedc-boundary">Human-governed decision support only.</p></div>}
                {historyDetailTab === "fit" && <div className="threedc-detail-pane"><div className="threedc-fit-grid">{productFits(result, run.input_snapshot?.workflow || workflow).map((item) => <article key={item.id}><FitIcon type={item.id} /><h3>{item.name}</h3><strong>{item.offer}</strong><div><i><b style={{ width: `${item.score}%` }} /></i><em>{item.score}</em></div><p>{item.rationale}</p></article>)}</div></div>}
              </>
            )}
          </div>
        </div>
      </section>
    );
  };

  const renderWorkflows = () => {
    const draft = workflowDraft || { workflow_uuid: "", name: "", description: "", modules: [...REQUIRED_MODULES, "social", "bank", "report"], focus: emptyFocus() };
    const focus = normalizeFocus(draft.focus);
    const updateDraft = (patch) => setWorkflowDraft((current) => ({ ...(current || draft), ...patch }));
    const updateFocus = (patch) => updateDraft({ focus: { ...focus, ...patch } });
    return (
      <section className="threedc-panel threedc-page-panel">
        <div className="threedc-page-heading"><div><span>Pipeline configuration</span><h2>Workflow builder</h2><p>Create reusable pipelines and define the intelligence each workflow should assess.</p></div></div>
        <div className="threedc-workflow-layout">
          <aside><button className="threedc-button primary full" onClick={() => setWorkflowDraft({ workflow_uuid: "", name: "", description: "", modules: [...REQUIRED_MODULES, "social", "bank", "report"], focus: emptyFocus() })}>New workflow</button>{workflows.map((item) => <button key={item.workflow_uuid} className={draft.workflow_uuid === item.workflow_uuid ? "active" : ""} onClick={() => setWorkflowDraft({ ...item, focus: normalizeFocus(item.focus) })}><strong>{item.name}</strong><span>{item.modules.length} modules · {item.focus?.market_segment || "General consumer"}</span></button>)}</aside>
          <form onSubmit={saveWorkflow}>
            <label className="threedc-field"><span>Workflow name</span><input required value={draft.name} onChange={(event) => updateDraft({ name: event.target.value })} /></label>
            <label className="threedc-field"><span>Description</span><textarea value={draft.description || ""} onChange={(event) => updateDraft({ description: event.target.value })} /></label>
            <section className="threedc-focus-editor">
              <div><span>Intelligence focus</span><h3>Define what this workflow should assess</h3><p>These settings guide the factual analysis and influence FIT prioritization.</p></div>
              <div className="threedc-focus-grid">
                <label className="threedc-field"><span>Consumer market segment</span><input value={focus.market_segment} onChange={(event) => updateFocus({ market_segment: event.target.value })} placeholder="e.g. Migrant worker" /></label>
                <div className="threedc-field"><span>Product focus</span><div className="threedc-product-options">{PRODUCT_OPTIONS.map(([value, label]) => <label key={value}><input type="checkbox" checked={focus.product_focus.includes(value)} onChange={() => {
                  let next = value === "all" ? ["all"] : focus.product_focus.filter((item) => item !== "all");
                  if (value !== "all") next = next.includes(value) ? next.filter((item) => item !== value) : [...next, value];
                  updateFocus({ product_focus: next.length ? next : ["all"] });
                }} />{label}</label>)}</div></div>
                <label className="threedc-field"><span>Key themes</span><input value={focus.themes.join(", ")} onChange={(event) => updateFocus({ themes: [event.target.value] })} onBlur={(event) => updateFocus({ themes: splitList(event.target.value) })} placeholder="financial inclusion, family support" /></label>
                <label className="threedc-field"><span>Keywords and signals</span><input value={focus.keywords.join(", ")} onChange={(event) => updateFocus({ keywords: [event.target.value] })} onBlur={(event) => updateFocus({ keywords: splitList(event.target.value) })} placeholder="remittance, payroll, transfer" /></label>
                <label className="threedc-field"><span>Topics to assess</span><input value={focus.topics.join(", ")} onChange={(event) => updateFocus({ topics: [event.target.value] })} onBlur={(event) => updateFocus({ topics: splitList(event.target.value) })} placeholder="income stability, loyalty engagement" /></label>
                <label className="threedc-field"><span>Analyst guidance</span><textarea value={focus.instructions} onChange={(event) => updateFocus({ instructions: event.target.value })} /></label>
              </div>
            </section>
            <div className="threedc-module-heading"><div><span>Pipeline modules</span><h3>Select analysis stages</h3></div><small>Execution order is managed automatically</small></div>
            <div className="threedc-module-grid">{MODULE_ORDER.map((key) => {
              const required = REQUIRED_MODULES.includes(key);
              const checked = required || draft.modules.includes(key);
              return <label key={key} className={`${checked ? "selected" : ""} ${required ? "required" : ""}`}><input type="checkbox" disabled={required} checked={checked} onChange={() => updateDraft({ modules: checked ? draft.modules.filter((item) => item !== key) : MODULE_ORDER.filter((item) => [...draft.modules, key].includes(item)) })} /><span><strong>{MODULES[key][0]}</strong><small>{MODULES[key][1]}</small></span><i>{required ? "Required" : MODULES[key][2]}</i></label>;
            })}</div>
            <div className="threedc-form-actions"><span>{message}</span><button className="threedc-button success" disabled={busy}>Save workflow</button></div>
          </form>
        </div>
      </section>
    );
  };

  const renderLlm = () => {
    const summary = llmSettings?.metrics?.summary || {};
    return (
      <section className="threedc-panel threedc-page-panel">
        <div className="threedc-page-heading"><div><span>AI configuration</span><h2>LLM Settings & usage</h2><p>Select an approved model and monitor token, latency and estimated-cost metrics.</p></div><button className="threedc-button" onClick={() => getThreeDCLlmSettings().then(setLlmSettings)}><RefreshIcon fontSize="small" /> Refresh metrics</button></div>
        <div className="threedc-llm-layout">
          <article><span>Run configuration</span><h3>Analysis model</h3><label className="threedc-field"><span>Model used for new runs</span><select value={selectedModel} onChange={(event) => setSelectedModel(event.target.value)}>{(llmSettings?.models || []).map((item) => <option key={item.id} value={item.id}>{item.id}{item.is_default ? " · server default" : ""}</option>)}{(llmSettings?.additional_providers || []).map((item) => <option key={`${item.provider}-${item.model}`} disabled>{item.model} · {item.provider} · connector required</option>)}</select></label><button className="threedc-button primary" onClick={() => { window.localStorage.setItem("3dc.llmModel", selectedModel); setMessage(`New runs will use ${selectedModel}.`); }}>Use selected model</button></article>
          <article><span>AI telemetry</span><h3>Usage totals</h3><div className="threedc-usage-grid"><Metric label="Requests" value={Number(summary.requests || 0).toLocaleString()} /><Metric label="Input tokens" value={Number(summary.input_tokens || 0).toLocaleString()} /><Metric label="Output tokens" value={Number(summary.output_tokens || 0).toLocaleString()} /><Metric label="Reasoning tokens" value={Number(summary.reasoning_tokens || 0).toLocaleString()} /><Metric label="Average latency" value={`${Math.round(Number(summary.average_latency_ms || 0)).toLocaleString()} ms`} /><Metric label="Estimated cost" value={`$${Number(summary.estimated_cost_usd || 0).toFixed(4)}`} /></div></article>
          <article className="wide"><span>Model breakdown</span><h3>Usage by model</h3><div className="threedc-model-table"><div><strong>Model</strong><strong>Requests</strong><strong>Input</strong><strong>Output</strong><strong>Latency</strong><strong>Cost</strong></div>{(llmSettings?.metrics?.by_model || []).map((item) => <div key={item.model}><strong>{item.model}</strong><span>{item.requests}</span><span>{Number(item.input_tokens).toLocaleString()}</span><span>{Number(item.output_tokens).toLocaleString()}</span><span>{Math.round(Number(item.average_latency_ms))} ms</span><span>${Number(item.estimated_cost_usd).toFixed(4)}</span></div>)}</div></article>
        </div>
      </section>
    );
  };

  return (
    <main className="page-container threedc-page">
      <header className="threedc-header">
        <div className="threedc-heading"><span className="threedc-mark">3C</span><div><span className="threedc-eyebrow">3D Intelligence</span><h1>3D C <small>Consumer Intelligence</small></h1><p>Build a consent-aware consumer profile from social, transactional and behavioral signals.</p></div></div>
        <div className="threedc-actions"><span className={`threedc-ai-pill ${status?.ai?.configured ? "live" : ""}`}><i />{status?.ai?.configured ? `Live AI · ${selectedModel || status.ai.model}` : "AI status unavailable"}</span><button className="threedc-button" onClick={reset}>Reset</button><button className="threedc-button" onClick={() => navigate("/3dc/settings")}><SettingsIcon fontSize="small" /> Settings</button></div>
      </header>
      <nav className="threedc-app-tabs">{[["analyze", "Analyze"], ["history", "History"], ["workflows", "Workflows"], ["llm", "LLM Settings"]].map(([key, label]) => <button key={key} className={appTab === key ? "active" : ""} onClick={() => changeAppTab(key)}>{label}</button>)}</nav>
      {error && <div className="threedc-error">{error}<button onClick={() => setError("")}>×</button></div>}
      {appTab === "analyze" && renderAnalyze()}
      {appTab === "history" && renderHistory()}
      {appTab === "workflows" && renderWorkflows()}
      {appTab === "llm" && renderLlm()}
    </main>
  );
}

export default ThreeDC;
